<template>
  <div
    ref="revealEl"
    :style="{
      opacity: isVisible ? 1 : props.opacity,
      transform: isVisible ? 'none' : getInitialTransform(),
      transitionProperty: 'opacity, transform',
      transitionDelay: `${delay}ms`,
      transitionDuration: `${duration}ms`,
      transitionTimingFunction: easing,
      willChange: 'opacity, transform',
    }"
  >
    <slot></slot>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  threshold: {
    type: Number,
    default: 0.1,
  },
  delay: {
    type: Number,
    default: 0,
  },
  duration: {
    type: Number,
    default: 800,
  },
  direction: {
    type: String,
    default: 'up',
    validator: (value) => ['up', 'down', 'left', 'right'].includes(value),
  },
  distance: {
    type: Number,
    default: 30,
  },
  scale: {
    type: Number,
    default: 1,
  },
  opacity: {
    type: Number,
    default: 0,
  },
  easing: {
    type: String,
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
})

const isVisible = ref(false)
const revealEl = ref(null)
let observer = null

const getInitialTransform = () => {
  const transforms = []

  // 方向变换
  switch (props.direction) {
    case 'up':
      transforms.push(`translateY(${props.distance}px)`)
      break
    case 'down':
      transforms.push(`translateY(-${props.distance}px)`)
      break
    case 'left':
      transforms.push(`translateX(${props.distance}px)`)
      break
    case 'right':
      transforms.push(`translateX(-${props.distance}px)`)
      break
  }

  // 缩放
  if (props.scale !== 1) {
    transforms.push(`scale(${props.scale})`)
  }

  return transforms.join(' ')
}

onMounted(() => {
  observer = new IntersectionObserver(
    ([entry]) => {
      isVisible.value = entry.isIntersecting
    },
    {
      threshold: props.threshold,
      rootMargin: '50px',
    }
  )

  if (observer && revealEl.value) {
    observer.observe(revealEl.value)
  }
})

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
.scroll-reveal-enter-active {
  transition-property: opacity, transform;
}

div {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}
</style>
